import { useState } from 'react';
import zxcvbn from 'zxcvbn';
import { map, find, filter } from 'lodash';
import { getOrganizationId } from '@flux/stores/cxStore';
import { PasswordInput, JSONValidator } from '@components/Inputs';
import infoTypes from './infoTypes.json';
import incomingChannels from './incomingChannels.json';
import envJson from '../../../../.env.json';
import { WrapperEntitySelector } from '@components/Inputs';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { Button, Grid, TextInput, Modal, Select, Checkbox, Typography, GridContainerFullHeight } from '@ts/storybook';
import { IForm } from '@ts/ui-models';

interface IChannelFormProps extends IForm {}

const ChannelForm = (props: IChannelFormProps) => {
  const { mode, values, onChange, onCallbackChange } = props;
  const [showModalPwd, setShowModalPwd] = useState(false);
  const [pwdStr, setPwdStr] = useState<
    | {
        password: string;
        score: number;
        feedback: {
          warning: string;
        };
      }
    | undefined
  >(undefined);
  const [auxPwd, setAuxPwd] = useState(null);

  const isViewMode = props.mode === 'view';
  const fStrictDisabled = props.mode !== 'new';

  //#region Handler changes functions
  const onChangeInfoType = infoTypeId => {
    onCallbackChange('infoTypeId')(infoTypeId);

    if (values && values.channelTypeId && values.bound === 'inbound') {
      const urlConfig = incomingChannels[values.channelTypeId];
      if (urlConfig) onCallbackChange('topic')(buildURL(urlConfig.url, values.endpoint));
    }
    if (values && infoTypeId && values.bound === 'outbound') {
      onCallbackChange('topic')(generateTopic(infoTypeId));
    }
  };
  const onChangeGeneric = (model, entity) => {
    if (model != null) {
      onCallbackChange(entity + 'Id')(model.getID());
      onCallbackChange(entity)(model);
    } else {
      onCallbackChange(entity + 'Id')(null);
      onCallbackChange(entity)(null);
    }
  };

  const _onChangePassword = event => {
    const password = event.target.value.trim();
    onCallbackChange('pass')(password);
    setPwdStr(zxcvbn(password));
  };

  const onJSONValidated = jsonValue => {
    onChange('infoTypeConfig', jsonValue);
  };

  const validate = () => {
    let error = '';
    if (values.pass !== values.passwordConfirmation) {
      error = 'Passwords must be equal';
    }
    return error;
  };

  const generateTopic = (infoTypeId: string) => {
    return find(infoTypes, { value: infoTypeId })?.outboundTopic?.replace('$organizationId', getOrganizationId());
  };

  function buildURL(url, endpoint) {
    return url.replace('$endpoint', endpoint).replace('$hostname', envJson.WSS_HOST || window.location.hostname);
  }

  const _shouldDisableModalOk = () => {
    let shouldEnable =
      values.pass &&
      // this.state.pwdStr && this.state.pwdStr.score > 2 &&
      values.pass === values.passwordConfirmation;

    return !shouldEnable;
  };
  const _handleModalCancel = () => {
    onCallbackChange('pass')(auxPwd);
    _closeModalPwd();
  };

  const _closeModalPwd = () => {
    setShowModalPwd(false);
  };

  const _openModalPwd = () => {
    setShowModalPwd(true);
    setAuxPwd(values.pass);
    onCallbackChange('pass')(null);
  };
  //#endregion

  //#region Render
  const renderMain = () => {
    return (
      <Grid container direction="column" spacing={2} size={{ xs: 12, md: 4 }}>
        <TextInput
          isReadOnly={isViewMode}
          label="Channel name"
          value={values.name}
          onChangeValue={onCallbackChange('name')}
          required={props.requiredFields?.includes('name')}
        />
        <Select
          label="Purpose"
          options={[
            { label: 'Inbound', value: 'inbound' },
            { label: 'Outbound', value: 'outbound' },
          ]}
          value={values.bound || 'outbound'}
          onChange={onCallbackChange('bound')}
          disabled={fStrictDisabled}
        />
        <WrapperEntitySelector
          searchComponent={generateSearchEntitySelector('channelType')}
          value={values.channelType}
          onChangeEntity={channel => onChangeGeneric(channel, 'channelType')}
          label={'Channel type'}
          disabled={fStrictDisabled}
        />
      </Grid>
    );
  };

  const renderBoundOptions = () => {
    const { bound, channelTypeId } = values;
    if (bound === 'inbound') {
      switch (channelTypeId) {
        case 'http':
        case 'mqtt':
        case 'amqp091':
          return _renderEndpoint();
        default:
          return;
      }
    } else if (bound === 'outbound') {
      return _renderOutboundSelection();
    }
  };

  const renderInfoTypeConfig = () => {
    const { infoTypeId } = values;
    if (!infoTypeId) return;
    const splitInfoTypeId = infoTypeId.split('.');
    switch (splitInfoTypeId[0]) {
      case 'readings':
        return (
          <Grid size={{ xs: 12, md: 8 }} mt={2}>
            {infoTypeId === 'readings' && (
              <JSONValidator
                label={'Info type configuration'}
                placeholder={[
                  {
                    unitName: 'BCN Coordinates',
                    originReadingTypeId: 'locationGPSLatLonCoordinates',
                    outboundReadingTypeId: 'locationLocalCoordinates',
                  },
                  { unitName: 'Celsius', outboundReadingTypeId: 'temperature' },
                ]}
                value={values.infoTypeConfig}
                disabled={isViewMode}
                onJSONValidated={onJSONValidated}
              />
            )}
          </Grid>
        );
      case 'processes':
        const filteredInfoTypes = map(
          filter(infoTypes, v => v.value?.startsWith('processes.')),
          (v, k) => ({
            label: v.label,
            value: v.value,
          })
        );
        return (
          <Grid size={{ xs: 12, md: 4 }} mt={2}>
            <Select
              label="Types"
              value={infoTypeId}
              onChange={onChangeInfoType}
              options={filteredInfoTypes}
              disabled={fStrictDisabled}
            />
          </Grid>
        );
    }

    return;
  };

  const _renderOutboundSelection = () => {
    const { infoTypeId } = values;
    const splitInfoTypeId = infoTypeId?.split('.') ?? [];
    const filteredInfoTypes = map(
      filter(infoTypes, v => !v.value?.startsWith('processes.')),
      (v, k) => ({
        label: v.label,
        value: v.value,
      })
    );
    return (
      <>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Select
              label={'Information type'}
              disabled={fStrictDisabled}
              value={splitInfoTypeId[0] || ''}
              onChange={onChangeInfoType}
              options={filteredInfoTypes}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            {infoTypeId && (
              <div>
                <Typography variant="subtitle1" text="Data will be published into" />
                <Typography variant="subtitle2" text={values.topic} />
              </div>
            )}
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          {renderInfoTypeConfig()}
        </Grid>
      </>
    );
  };

  const renderTypeSpecificForm = () => {
    const { channelTypeId } = values;
    switch (channelTypeId) {
      case 'email':
        return _renderEmail();
      case 'database':
        return _renderDatabase();
      default:
        return;
    }
  };

  const _renderEmail = () => {
    return (
      <Grid container direction={'column'} size={4} spacing={2}>
        {_renderHostPort()}
        {_renderSender()}
        {_renderUserPass()}
        <Grid container direction="row" spacing={2} size={12}>
          <Checkbox
            isReadOnly={isViewMode}
            checked={values.secure || false}
            onChangeValue={onCallbackChange('secure')}
            label="Secure"
            labelPosition="start"
            className="pt-5 text-[15px]"
          />
          <Checkbox
            isReadOnly={isViewMode}
            checked={values.ignoreTLS || false}
            onChangeValue={onCallbackChange('ignoreTLS')}
            label="IgnoreTLS"
            labelPosition="start"
            className="pt-5 text-[15px]"
          />
          <Checkbox
            isReadOnly={isViewMode}
            checked={values.requireTLS || false}
            onChangeValue={onCallbackChange('requireTLS')}
            label="RequireTLS"
            labelPosition="start"
            className="pt-5 text-[15px]"
          />
        </Grid>
      </Grid>
    );
  };

  const _renderEndpoint = () => {
    const { channelTypeId, endpoint } = values;
    return (
      <>
        <TextInput
          isReadOnly={isViewMode}
          label={'Endpoint'}
          value={values.endpoint}
          onChangeValue={onCallbackChange('endpoint')}
          required={props.requiredFields?.includes('endpoint')}
        />
        <br />
        {_renderURL(channelTypeId, endpoint)}
      </>
    );
  };

  const _renderURL = (channelTypeId, endpoint) => {
    const urlConfig = incomingChannels[channelTypeId];
    return (
      <>
        {urlConfig && endpoint && (
          <>
            <Typography variant="caption" color="textSecondary" text={urlConfig.urlText} />
            <Typography variant="subtitle2" color="textPrimary" text={buildURL(urlConfig.url, endpoint)} />
            <Typography
              variant="caption"
              color="textSecondary"
              text="Please ask your administrator for the inbound routing key"
            />
          </>
        )}
      </>
    );
  };

  const _renderHostPort = () => {
    return (
      <>
        <TextInput isReadOnly={isViewMode} label={'Host'} value={values.host} onChangeValue={onCallbackChange('host')} required />
        <TextInput isReadOnly={isViewMode} label={'Port'} value={values.port} onChangeValue={onCallbackChange('port')} required />
      </>
    );
  };

  const _renderUserPass = () => {
    return (
      <>
        <TextInput isReadOnly={isViewMode} label={'User'} value={values.user} onChangeValue={onCallbackChange('user')} required />
        {mode === 'new' && _renderPasswords()}
        {mode === 'edit' && _renderChangePwd()}
        {_renderModal()}
      </>
    );
  };

  const _renderSender = () => {
    return (
      <TextInput
        isReadOnly={isViewMode}
        label={'Sender'}
        value={values.sender}
        onChangeValue={onCallbackChange('sender')}
        required
      />
    );
  };

  const _renderChangePwd = () => {
    return <Button variant="outlined" onClick={_openModalPwd} disabled={isViewMode} label="Change password" />;
  };

  const _renderPasswords = () => {
    return (
      <>
        <PasswordInput
          disabled={isViewMode}
          label={'Password'}
          value={values.pass || ''}
          onChange={_onChangePassword}
          required
          autoComplete="new-password"
          pwdStr={pwdStr}
        />
        <TextInput
          disabled={isViewMode}
          label={'Repeat new password'}
          value={values.passwordConfirmation}
          type="password"
          onChangeValue={onCallbackChange('passwordConfirmation')}
          required
          errorHint={validate()}
          autoComplete="new-password"
        />
        <Typography variant="body2" style={{ color: 'gray' }} text="Starting and ending whitespaces will be trimmed" />
      </>
    );
  };

  const _renderDatabase = () => {
    let dmbsOptions = [
      {
        fnValue: () => 'postgres',
        fnLabel: () => 'PostgreSQL',
      },
    ];
    return (
      <Grid container size={4} spacing={2}>
        <Select
          label="Database management system"
          isReadOnly={isViewMode}
          value={values.dbms || 'postgres'}
          onChange={onCallbackChange('dbms')}
          options={dmbsOptions}
        />
        {_renderHostPort()}
        {_renderUserPass()}
        <TextInput
          isReadOnly={isViewMode}
          label="Database name"
          value={values.dbName}
          onChangeValue={onCallbackChange('dbName')}
          required
        />
        <TextInput
          isReadOnly={isViewMode}
          label="Database table name"
          value={values.tableName}
          onChangeValue={onCallbackChange('tableName')}
          required
        />
        <TextInput
          isReadOnly={isViewMode}
          label="Target system"
          value={values.targetSystem}
          onChangeValue={onCallbackChange('targetSystem')}
          required
        />
      </Grid>
    );
  };

  const _renderModal = () => {
    return (
      <Modal
        withHeader
        open={showModalPwd}
        title="Change password"
        labelOk="Change password"
        onClose={_handleModalCancel}
        onSubmit={_closeModalPwd}
        disabled={_shouldDisableModalOk()}
        fullWidth
      >
        <Grid size={{ xs: 12 }}>{_renderPasswords()}</Grid>
      </Modal>
    );
  };
  //#endregion

  return (
    <GridContainerFullHeight>
      <Grid container spacing={6} direction="column">
        {renderMain()}
        {renderTypeSpecificForm()}
        {renderBoundOptions()}
      </Grid>
    </GridContainerFullHeight>
  );
};

export default ChannelForm;
